/**
 * Pure "what ts should be on screen right now" decision for the mosaic's
 * single-timeline transport (live latest vs. scrub/loop position) — pulled
 * out of mosaicController.ts's refresh() so it's unit-testable without
 * instantiating the controller (which spins up a real Worker via
 * FusionDecoder, unavailable under vitest's `environment: "node"` — the
 * same reason fusionController.ts itself has no direct unit test; see that
 * file's sibling fusionGeo.ts/fusionCompositor.ts for the established
 * pattern of testing the extracted PURE logic instead).
 */
import type { MosaicHotManifest } from "./mosaicTypes";
import { frameAtOrBefore } from "./scrub";

/** Live mode: the manifest's own `latest.ts`, falling back to the newest
 * frame in the trailing list if `latest` itself is ever absent (mirrors
 * every other manifest consumer in this client, e.g. app.ts's onManifest).
 * Scrub/loop mode: the mosaic's own single frames list, nearest at-or-before
 * `lastScrubMs` (scrub.ts's frameAtOrBefore — the SAME function every other
 * scrub axis in this client uses, just against one manifest instead of N).
 * Null whenever there's nothing to show yet (no manifest, or scrubbed
 * earlier than the mosaic's own trailing-window coverage). */
export function resolveTargetTs(manifest: MosaicHotManifest | null, liveMode: boolean, lastScrubMs: number | null): string | null {
  if (!manifest) return null;
  if (liveMode) return manifest.latest?.ts ?? manifest.frames.at(-1)?.ts ?? null;
  if (lastScrubMs === null) return null;
  return frameAtOrBefore(manifest.frames, lastScrubMs)?.ts ?? null;
}
